// Runtime config the SPA fetches from /bootstrap.json at startup instead of
// having Vite inline it at build time. This lets one built bundle move
// between OIDC AS environments without a rebuild — only the deploy step's
// bootstrap.json changes.
//
// Only the AS base URL lives here. The app origin to redirect back to is
// NOT part of this config: this auth SPA can serve more than one client,
// each possibly on a different app origin, so there is no single static
// value to configure — see resolveAppOrigin() in
// @content-buddy/auth's hooks/oidc/beginLogin.ts, which resolves it
// per-client from the AS's client registry instead.
//
// bootstrap.json is environment-specific and NOT part of the built bundle:
// - heph deploy (frontend/BUILD) writes it to the S3 bucket after the sync.
// - the Docker images write it at build time, next to `npm run build:*` (see
//   packages/{app,auth}/Dockerfile) — a runtime container env var cannot
//   change an already-built image layer, so this path stays build-time.
// - the Vite dev server serves it from a dev-only middleware
//   (packages/{app,auth}/vite.config.ts) sourced from VITE_OIDC_URL in
//   .env.local, same as before this moved to runtime.
//
// A fetch failure (offline, missing file, bad JSON) is not fatal: callers
// fall back to same-origin behavior, same as an unset env var did before.
export interface RuntimeConfig {
  oidcUrl: string;
}

const EMPTY: RuntimeConfig = { oidcUrl: "" };

let config: RuntimeConfig = EMPTY;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const resp = await fetch("/bootstrap.json");
    if (resp.ok) {
      const body = (await resp.json()) as Partial<RuntimeConfig>;
      config = { oidcUrl: body.oidcUrl ?? "" };
    }
  } catch {
    // Network or parse failure: keep whatever config is already set.
  }
  return config;
}

export function getRuntimeConfig(): RuntimeConfig {
  return config;
}

// setRuntimeConfig lets tests seed config without a network round trip.
export function setRuntimeConfig(next: Partial<RuntimeConfig>): void {
  config = { ...config, ...next };
}

export function resetRuntimeConfig(): void {
  config = EMPTY;
}
